'use client'

import React from 'react'
import { Check, Copy, ExternalLink } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { sideLabels } from '../lib/amm'
import {
	formatAge,
	formatQty,
	formatSupplyPct,
	formatUsdPrice,
	numCompact,
	truncateMiddle,
	usdCompact
} from '../lib/format'
import useAssetHolders from '../lib/useAssetHolders'
import useAssetTrades from '../lib/useAssetTrades'
import type { TwetchFeedData } from '../lib/use-twetch-feed'
import useTwetchIdentities from '../lib/useTwetchIdentities'
import { postPermalink } from '../lib/twetch-media'
import { useAlkanes } from '../lib/store'
import type { AssetRow } from '../lib/useAssets'
import HistoryList from './history-list'
import { Avatar, PostText } from './social-feed'
import UserHoverCard from './user-hover-card'
import { openTwetchUserProfile } from '../lib/twetch-profile'

/// The asset's tables — Holders | Swaps | Thesis | My history — as ONE
/// component both layouts mount: the desktop workspace's bottom strip
/// (filling its flex slot) and the phone's card under the chart (a fixed
/// height, so infinite scroll still has a container to scroll in). Each
/// table fetches only while it is the visible tab — the server caches one
/// page per asset either way, but the client still shouldn't poll a table
/// nobody is looking at.
export default function AssetTables({
	asset,
	myAddress,
	thesisFeed,
	holdersCount,
	className
}: {
	asset: AssetRow | null
	myAddress: string | null
	thesisFeed: TwetchFeedData
	holdersCount: number | null
	className?: string
}) {
	const [bottomTab, setBottomTab] = React.useState<'holders' | 'swaps' | 'thesis' | 'history'>(
		'holders'
	)
	const swapsTape = useAssetTrades(bottomTab === 'swaps' ? asset?.id ?? null : null)
	const holdersTable = useAssetHolders(bottomTab === 'holders' ? asset?.id ?? null : null)
	const stats = { holders: holdersCount }

	return (
		<div className={cn('flex flex-col overflow-hidden rounded-2xl bg-card', className)}>
				<div
					role="tablist"
					aria-label="Asset tables"
					className="z-20 flex shrink-0 gap-1 border-b border-border/60 bg-card px-3"
				>
					{(
						[
							[
								'holders',
								`Holders${
									stats?.holders !== null && stats?.holders !== undefined
										? ` (${stats.holders.toLocaleString('en-US')})`
										: ''
								}`
							],
							['swaps', 'Swaps'],
							['thesis', 'Thesis'],
							['history', 'My history']
						] as const
					).map(([id, label]) => {
						const on = bottomTab === id
						return (
							<button
								key={id}
								type="button"
								role="tab"
								aria-selected={on}
								data-testid={`alkanes-table-tab-${id}`}
								onClick={() => setBottomTab(id)}
								className={cn(
									'relative px-3 py-2.5 text-sm font-semibold transition-colors',
									on ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
								)}
							>
								{label}
								{on && (
									<span
										aria-hidden="true"
										className="absolute inset-x-2 bottom-0 h-0.5 rounded-full bg-primary"
									/>
								)}
							</button>
						)
					})}
				</div>
				<div
					data-testid="alkanes-table-scroll"
					className="min-h-0 flex-1 overflow-auto overscroll-contain"
					onScroll={(e) => {
						// Infinite scroll for the live tables: within two
						// viewport-heights of the bottom, ask for the next page.
						// The hooks gate re-entry themselves.
						const el = e.currentTarget
						if (el.scrollHeight - el.scrollTop - el.clientHeight < el.clientHeight * 2) {
							if (bottomTab === 'swaps') {
								swapsTape.loadMore()
							} else if (bottomTab === 'holders') {
								holdersTable.loadMore()
							}
						}
					}}
				>
					<div className="min-w-0">
						{bottomTab === 'swaps' ? (
							<SwapsTape
								tape={swapsTape}
								symbol={asset?.name || asset?.id || ''}
								assetId={asset?.id ?? null}
							/>
						) : bottomTab === 'holders' ? (
							<HoldersTable
								table={holdersTable}
								symbol={asset?.name || asset?.id || ''}
								myAddress={myAddress}
							/>
						) : bottomTab === 'history' ? (
							// The connected wallet's own history — every asset, the
							// ledger's exact rows plus whatever is in the mempool and
							// whatever this browser just signed.
							<HistoryList address={myAddress} own compact />
						) : (
							<ThesisTable feed={thesisFeed} />
						)}
					</div>
				</div>
			</div>
	)
}

/// The public swap tape — rows of the same espo page the 24h buy/sell
/// aggregates come from, newest first. Three claims kept distinct: rows,
/// zero rows in a LOADED window ("no swaps since…"), and no window at all
/// (upstream down or an API without the endpoint — never shown as empty).
function SwapsTape({
	tape,
	symbol,
	assetId
}: {
	tape: ReturnType<typeof useAssetTrades>
	symbol: string
	assetId: string | null
}) {
	const labels = sideLabels(assetId)
	const openProfile = useAlkanes((s) => s.openProfile)
	const identities = useTwetchIdentities((tape.trades ?? []).map((t) => t.swapper))
	// State-held clock (pools-status pattern): a render-time Date.now() is
	// impure under the compiler, and the tape refreshes every 60s anyway so
	// a per-mount stamp ticking once a minute keeps ages honest enough.
	const [now, setNow] = React.useState(() => Date.now())
	React.useEffect(() => {
		const timer = setInterval(() => setNow(Date.now()), 60_000)
		return () => clearInterval(timer)
	}, [])
	const [copiedId, setCopiedId] = React.useState<string | null>(null)
	const copy = async (addr: string) => {
		try {
			await navigator.clipboard.writeText(addr)
			setCopiedId(addr)
			setTimeout(() => setCopiedId((c) => (c === addr ? null : c)), 1500)
		} catch {
			// Clipboard blocked — the full address is still in the title attr.
		}
	}
	if (tape.unavailable) {
		return (
			<p className="px-4 py-8 text-center text-xs leading-relaxed text-muted-foreground">
				The swap tape isn’t available right now. Your own swaps are still on the chart overlay and
				in your Profile.
			</p>
		)
	}
	if (tape.isLoading || !tape.trades) {
		return (
			<div className="space-y-2 p-4">
				{[0, 1, 2, 3].map((i) => (
					<Skeleton key={i} className="h-6 w-full" />
				))}
			</div>
		)
	}
	return (
		<React.Fragment>
			<div
				aria-hidden="true"
				data-testid="alkanes-swaps-header"
				className="sticky top-0 z-10 grid grid-cols-[0.7fr_0.5fr_1.1fr_0.9fr_0.9fr_1.5fr] gap-2 border-b border-border/40 bg-card px-4 py-2 text-xxs font-medium text-muted-foreground"
			>
				<span>Time</span>
				<span>Side</span>
				<span className="text-right">Amount</span>
				<span className="text-right">Price</span>
				<span className="text-right">Value</span>
				<span className="text-right">Wallet</span>
			</div>
			{tape.trades.length === 0 ? (
				<p className="px-4 py-8 text-center text-xs leading-relaxed text-muted-foreground">
					No swaps in the indexed window.
				</p>
			) : (
				<div data-testid="alkanes-swaps-tape">
					{tape.trades.map((t, i) => (
						<div
							key={`${t.ts}-${i}`}
							className="grid grid-cols-[0.7fr_0.5fr_1.1fr_0.9fr_0.9fr_1.5fr] items-center gap-2 border-b border-border/20 px-4 py-2 text-xs tabular-nums"
						>
							{/* Own-tape rows link their tx to the explorer; espo
							    fallback rows have no txid and stay plain text. */}
							{t.txid ? (
								<a
									href={`https://espo.sh/tx/${encodeURIComponent(t.txid)}`}
									target="_blank"
									rel="noreferrer"
									title="View transaction on espo.sh"
									className="group inline-flex items-center gap-1 text-muted-foreground transition-colors hover:text-foreground hover:underline"
								>
									<span>{formatAge(now - t.ts * 1000)}</span>
									<ExternalLink className="size-3 shrink-0" aria-hidden="true" />
								</a>
							) : (
								<span className="text-muted-foreground">{formatAge(now - t.ts * 1000)}</span>
							)}
							<span
								className={cn(
									'font-semibold',
									t.side === 'buy' ? 'text-success-ink' : 'text-destructive-ink'
								)}
							>
								{t.side === 'buy' ? labels.buy : labels.sell}
							</span>
							{/* Dynamic decimals: a real 0.001 frBTC leg must render
							    "0.001", never round to the false zero "0". */}
							<span className="text-right">
								{t.amount !== null ? `${formatQty(t.amount)} ${symbol}` : '—'}
							</span>
							{/* The server labels each price's provenance; anything not
							    espo's own historical print is an estimate from our own
							    series and reads as one ("≈", explained on hover). */}
							<span
								className="text-right text-muted-foreground"
								title={
									t.price_source === 'current'
										? 'Estimated at the current price (trade is under 48h old)'
										: t.price_source === 'candle'
										? 'Estimated from the daily candle on the trade’s date'
										: undefined
								}
							>
								{t.price_usd !== null
									? `${t.price_source !== 'espo' ? '≈' : ''}${formatUsdPrice(t.price_usd)}`
									: '—'}
							</span>
							<span className="text-right">
								{t.value_usd !== null
									? `${t.price_source !== 'espo' ? '≈' : ''}${usdCompact(t.value_usd)}`
									: '—'}
							</span>
							{/* Same address idiom as Holders: in-app profile link + copy —
							    wearing the Twetch identity when the wallet has one. */}
							{t.swapper ? (
								<span className="flex min-w-0 items-center justify-end gap-1">
									<UserHoverCard userId={identities[t.swapper]?.id} user={identities[t.swapper]}>
									<a
										href={`/alkanes/profile/${encodeURIComponent(t.swapper)}`}
										title={`View ${t.swapper}'s profile`}
										onClick={(e) => {
											// Cmd/ctrl/shift-click keeps the browser's open-in-tab;
											// a plain click stays inside the app.
											if (e.metaKey || e.ctrlKey || e.shiftKey) {
												return
											}
											e.preventDefault()
											openProfile(t.swapper!)
										}}
										className={cn(
											'group inline-flex min-w-0 items-center gap-1 transition-colors hover:text-foreground hover:underline',
											!identities[t.swapper] && 'font-mono'
										)}
									>
										{identities[t.swapper] ? (
											<React.Fragment>
												<Avatar
													user={identities[t.swapper]}
													userId={identities[t.swapper].id}
													size={16}
												/>
												<span className="truncate font-semibold">{identities[t.swapper].name}</span>
											</React.Fragment>
										) : (
											<span className="truncate">{truncateMiddle(t.swapper)}</span>
										)}
									</a>
									</UserHoverCard>
									<button
										type="button"
										onClick={() => copy(t.swapper!)}
										aria-label={copiedId === t.swapper ? 'Copied' : `Copy address ${t.swapper}`}
										className="grid size-6 shrink-0 place-items-center rounded text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
									>
										{copiedId === t.swapper ? (
											<Check className="size-3 text-success-ink" />
										) : (
											<Copy className="size-3" />
										)}
									</button>
								</span>
							) : (
								<span className="text-right text-muted-foreground">—</span>
							)}
						</div>
					))}
				</div>
			)}
			{tape.oldestTs !== null && (
				<p className="px-4 py-2 text-right text-xxs text-muted-foreground">
					{/* The window's honest lower bound — espo serves one page. */}
					since {formatAge(now - tape.oldestTs * 1000)} ago
				</p>
			)}
		</React.Fragment>
	)
}

function ThesisTable({ feed }: { feed: TwetchFeedData }) {
	const [now, setNow] = React.useState(() => Date.now())
	React.useEffect(() => {
		const timer = window.setInterval(() => setNow(Date.now()), 60_000)
		return () => window.clearInterval(timer)
	}, [])
	if (feed.failed) {
		return (
			<div className="px-4 py-8 text-center text-xs text-muted-foreground">
				<p>Theses aren’t available right now.</p>
				<button
					type="button"
					onClick={feed.retry}
					className="mt-2 rounded-lg bg-secondary px-3 py-2 font-semibold text-foreground"
				>
					Retry
				</button>
			</div>
		)
	}
	if (feed.loadingInitial) {
		return (
			<div className="space-y-2 p-4">
				{[0, 1, 2].map((index) => (
					<Skeleton key={index} className="h-10 w-full" />
				))}
			</div>
		)
	}
	return (
		<React.Fragment>
			<div
				aria-hidden="true"
				className="sticky top-0 z-10 grid grid-cols-[1fr_0.55fr_2.4fr] gap-3 border-b border-border/40 bg-card px-4 py-2 text-xxs font-medium text-muted-foreground"
			>
				<span>Author</span>
				<span>Posted</span>
				<span>Thesis</span>
			</div>
			{feed.posts.length === 0 ? (
				<p className="px-4 py-8 text-center text-xs text-muted-foreground">
					No theses for this token yet.
				</p>
			) : (
				<div data-testid="alkanes-thesis-table">
					{feed.posts.map((post) => {
						const user = feed.users[String(post.userId)]
						const postedAt = post.postedAtMs ?? post.createdAtMs
						return (
							<div
								key={post.id}
								className="grid grid-cols-[1fr_0.55fr_2.4fr] items-center gap-3 border-b border-border/20 px-4 py-2.5 text-xs transition-colors hover:bg-secondary/30"
							>
								<UserHoverCard userId={post.userId} user={user}>
									<button
										type="button"
										onClick={() => void openTwetchUserProfile(post.userId)}
										aria-label={`Open ${user?.name ?? `#${post.userId}`}'s profile`}
										className="flex min-w-0 items-center gap-2 rounded-lg text-left transition-opacity hover:opacity-80"
									>
										<Avatar user={user} userId={post.userId} size={28} />
										<span className="truncate font-semibold hover:underline">
											{user?.name ?? `#${post.userId}`}
										</span>
									</button>
								</UserHoverCard>
								<a
									href={postPermalink(post)}
									target="_blank"
									rel="noreferrer"
									className="tabular-nums text-muted-foreground hover:text-foreground"
								>
									{postedAt ? `${formatAge(Math.max(0, now - postedAt))} ago` : '—'}
								</a>
								<span className="line-clamp-2 min-w-0 break-words leading-relaxed">
									{post.content?.trim() ? (
										<PostText text={post.content} smartTags={post.smartTags} />
									) : (
										<span className="text-muted-foreground">Post on Twetch</span>
									)}
								</span>
							</div>
						)
					})}
				</div>
			)}
		</React.Fragment>
	)
}

/// The ranked holder table — alkanode's balance book, labelled. Three claims
/// kept distinct (the tape's rule): rows, zero rows in a LOADED book, and no
/// book at all (upstream down or an API without the endpoint — never shown
/// as empty). Columns are only what is real for a holder: identity, position,
/// share of supply, dollar value. fomo's PnL / avg-entry / thesis columns
/// need per-address trade history nobody indexes, so they aren't rendered —
/// a wall of "—" would be claiming the columns exist.
function HoldersTable({
	table,
	symbol,
	myAddress
}: {
	table: ReturnType<typeof useAssetHolders>
	symbol: string
	myAddress: string | null
}) {
	const openProfile = useAlkanes((s) => s.openProfile)
	const identities = useTwetchIdentities(
		(table.holders ?? []).map((h) => (h.holder.kind === 'address' ? h.holder.id : null))
	)
	const [copiedId, setCopiedId] = React.useState<string | null>(null)
	const copy = async (addr: string) => {
		try {
			await navigator.clipboard.writeText(addr)
			setCopiedId(addr)
			setTimeout(() => setCopiedId((c) => (c === addr ? null : c)), 1500)
		} catch {
			// Clipboard blocked — the full address is still in the title attr.
		}
	}
	if (table.unavailable) {
		return (
			<p
				data-testid="alkanes-holders-unavailable"
				className="px-4 py-8 text-center text-xs leading-relaxed text-muted-foreground"
			>
				Holder positions aren’t available right now — the holder count above still is.
			</p>
		)
	}
	if (table.isLoading || !table.holders) {
		return (
			<div className="space-y-2 p-4">
				{[0, 1, 2, 3].map((i) => (
					<Skeleton key={i} className="h-6 w-full" />
				))}
			</div>
		)
	}
	return (
		<React.Fragment>
			<div
				aria-hidden="true"
				data-testid="alkanes-holders-header"
				className="sticky top-0 z-10 grid grid-cols-[1.8fr_1fr_0.8fr_1fr] gap-2 border-b border-border/40 bg-card px-4 py-2 text-xxs font-medium text-muted-foreground"
			>
				<span>Holder</span>
				<span className="text-right">Position</span>
				<span className="text-right">% supply</span>
				<span className="text-right">Value</span>
			</div>
			{table.holders.length === 0 ? (
				<p className="px-4 py-8 text-center text-xs leading-relaxed text-muted-foreground">
					No holders in the indexed book.
				</p>
			) : (
				<div data-testid="alkanes-holders-table">
					{table.holders.map((h) => {
						const isYou =
							h.holder.kind === 'address' && myAddress !== null && h.holder.id === myAddress
						return (
							<div
								key={`${h.rank}-${h.holder.id}`}
								className="grid grid-cols-[1.8fr_1fr_0.8fr_1fr] items-center gap-2 border-b border-border/20 px-4 py-2 text-xs tabular-nums"
							>
								<span className="flex min-w-0 items-center gap-1.5">
									<span className="w-6 shrink-0 text-muted-foreground">{h.rank}</span>
									{h.holder.kind === 'address' ? (
										<React.Fragment>
											<UserHoverCard userId={identities[h.holder.id]?.id} user={identities[h.holder.id]}>
											<a
												href={`/alkanes/profile/${encodeURIComponent(h.holder.id)}`}
												title={`View ${h.holder.id}'s profile`}
												onClick={(e) => {
													if (e.metaKey || e.ctrlKey || e.shiftKey) {
														return
													}
													e.preventDefault()
													openProfile(h.holder.id)
												}}
												className={cn(
													'group inline-flex min-w-0 items-center gap-1 transition-colors hover:text-foreground hover:underline',
													!identities[h.holder.id] && 'font-mono'
												)}
											>
												{identities[h.holder.id] ? (
													<React.Fragment>
														<Avatar
															user={identities[h.holder.id]}
															userId={identities[h.holder.id].id}
															size={16}
														/>
														<span className="truncate font-semibold">
															{identities[h.holder.id].name}
														</span>
													</React.Fragment>
												) : (
													<span className="truncate">{truncateMiddle(h.holder.id)}</span>
												)}
											</a>
											</UserHoverCard>
											<button
												type="button"
												onClick={() => copy(h.holder.id)}
												aria-label={
													copiedId === h.holder.id ? 'Copied' : `Copy address ${h.holder.id}`
												}
												className="grid size-6 shrink-0 place-items-center rounded text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
											>
												{copiedId === h.holder.id ? (
													<Check className="size-3 text-success-ink" />
												) : (
													<Copy className="size-3" />
												)}
											</button>
										</React.Fragment>
									) : (
										// A CONTRACT holding the token — a pool's own reserve,
										// the router vault. Presenting it as a whale wallet
										// would be a lie of omission; the chip says what it is.
										<a
											href={`https://espo.sh/alkane/${encodeURIComponent(h.holder.id)}`}
											target="_blank"
											rel="noreferrer"
											title={h.holder.id}
											className="inline-flex min-w-0 items-center gap-1 rounded-full bg-secondary/60 px-2 py-0.5 text-xxs font-medium text-muted-foreground transition-colors hover:text-foreground"
										>
											{h.icon && (
												 
												<img
													src={h.icon}
													alt=""
													width={14}
													height={14}
													className="size-3.5 shrink-0 rounded-full"
												/>
											)}
											<span className="truncate">{h.label ?? `contract ${h.holder.id}`}</span>
											<ExternalLink className="size-3 shrink-0" aria-hidden="true" />
										</a>
									)}
									{isYou && (
										<span
											data-testid="alkanes-holder-you"
											className="shrink-0 rounded-full bg-primary/15 px-1.5 py-0.5 text-xxs font-semibold text-primary"
										>
											you
										</span>
									)}
								</span>
								<span className="truncate text-right">
									{numCompact(Number(h.amount) / 1e8)} {symbol}
								</span>
								<span className="text-right text-muted-foreground">
									{formatSupplyPct(h.pct_of_supply)}
								</span>
								<span className="text-right">
									{h.value_usd !== null ? usdCompact(h.value_usd) : '—'}
								</span>
							</div>
						)
					})}
				</div>
			)}
			{table.hasMore && table.holders.length > 0 && (
				<p className="px-4 py-2 text-right text-xxs text-muted-foreground">
					{/* The book continues past this page — say where it stops. */}
					top {table.holders.length}
					{table.total !== null ? ` of ${table.total.toLocaleString('en-US')} holders` : ''}
				</p>
			)}
		</React.Fragment>
	)
}
